/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const enGB: LocalizationResource = {
  locale: 'en-GB',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copy & Close',
      formHint: "For security reasons, we won't allow you to view it again later.",
      formTitle: 'Copy your "{{name}}" API Key now',
    },
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: 'Back',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: 'Default',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Other impersonator device',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'Primary',
  badge__renewsAt: undefined,
  badge__requiresAction: 'Requires action',
  badge__startsAt: undefined,
  badge__thisDevice: 'This device',
  badge__trialEndsAt: undefined,
  badge__unverified: 'Unverified',
  badge__upcomingPlan: undefined,
  badge__userDevice: 'User device',
  badge__you: 'You',
  billing: {
    addPaymentMethod__label: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedMonthlyOnly: undefined,
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    keepFreeTrial: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    paymentMethods__label: undefined,
    highlightedPlanBadge: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle:
        'Contact your organisation administrator in order to have permissions to manage enterprise connections.',
      title: 'You do not have permission to manage enterprise connections',
    },
    navbar: {
      title: 'Configure Single Sign-On (SSO)',
    },
    selectProviderStep: {
      title: 'Select provider',
      subtitle: 'Select the provider you are going to set up SSO for.',
      body: {
        title: 'Select your identity provider',
        description: "We'll guide you through the detailed setup process next.",
      },
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: 'Custom SAML Provider',
      },
      warning: 'Once a provider is selected you cannot change again until the configuration is over',
    },
    verifyEmailDomainStep: {
      title: 'Verify email address',
      subtitle: 'Verify the email address you want to enable the enterprise connection on.',
      addEmailAddress: {
        formTitle: 'We need your email',
        formSubtitle: 'In order to start we will need your email address',
        inputPlaceholder: 'name@company.com',
        inputLabel: 'Email address',
      },
      emailCode: {
        formTitle: 'Verify your email address',
        formSubtitle: 'Enter the verification code sent to {{identifier}}',
        resendButton: "Didn't receive a code? Resend",
        verified: {
          title: 'We got your email',
          subtitle: "You've verified your email address with the following email",
          inputLabel: 'Verified email address',
        },
      },
      domainTaken: {
        title: 'This domain ({{domain}}) already has an SSO connection',
        subtitle: "Contact the application's administrator to get access through the existing connection.",
      },
    },
  },
  createOrganization: {
    formButtonSubmit: 'Create organisation',
    invitePage: {
      formButtonReset: 'Skip',
    },
    title: 'Create organisation',
  },
  dates: {
    lastDay: "Yesterday at {{ date | timeString('en-GB') }}",
    next6Days: "{{ date | weekday('en-GB','long') }} at {{ date | timeString('en-GB') }}",
    nextDay: "Tomorrow at {{ date | timeString('en-GB') }}",
    numeric: "{{ date | numeric('en-GB') }}",
    previous6Days: "Last {{ date | weekday('en-GB','long') }} at {{ date | timeString('en-GB') }}",
    sameDay: "Today at {{ date | timeString('en-GB') }}",
  },
  dividerText: 'or',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'Use another method',
  footerPageLink__help: 'Help',
  footerPageLink__privacy: 'Privacy',
  footerPageLink__terms: 'Terms',
  formButtonPrimary: 'Continue',
  formButtonPrimary__verify: 'Verify',
  formFieldAction__forgotPassword: 'Forgot password?',
  formFieldError__matchingPasswords: 'Passwords match.',
  formFieldError__notMatchingPasswords: "Passwords don't match.",
  formFieldError__verificationLinkExpired: 'The verification link expired. Please request a new link.',
  formFieldHintText__optional: 'Optional',
  formFieldHintText__slug: 'A slug is a human-readable ID that must be unique. It’s often used in URLs.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: 'Enter backup code',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Delete account',
  formFieldInputPlaceholder__emailAddress: 'Enter your email address',
  formFieldInputPlaceholder__emailAddress_username: 'Enter email or username',
  formFieldInputPlaceholder__emailAddresses: 'example@email.com, example2@email.com',
  formFieldInputPlaceholder__firstName: 'First name',
  formFieldInputPlaceholder__lastName: 'Last name',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: 'Organisation name',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'Enter your password',
  formFieldInputPlaceholder__phoneNumber: 'Enter your phone number',
  formFieldInputPlaceholder__username: 'Enter your username',
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'API key',
  formFieldLabel__apiKeyDescription: 'Description',
  formFieldLabel__apiKeyExpiration: 'Expiration',
  formFieldLabel__apiKeyName: 'Secret key name',
  formFieldLabel__automaticInvitations: 'Enable automatic invitations for this domain',
  formFieldLabel__backupCode: 'Backup code',
  formFieldLabel__confirmDeletion: 'Confirmation',
  formFieldLabel__confirmPassword: 'Confirm password',
  formFieldLabel__currentPassword: 'Current password',
  formFieldLabel__emailAddress: 'Email address',
  formFieldLabel__emailAddress_username: 'Email address or username',
  formFieldLabel__emailAddresses: 'Email addresses',
  formFieldLabel__firstName: 'First name',
  formFieldLabel__lastName: 'Last name',
  formFieldLabel__newPassword: 'New password',
  formFieldLabel__organizationDomain: 'Domain',
  formFieldLabel__organizationDomainDeletePending: 'Delete pending invitations and suggestions',
  formFieldLabel__organizationDomainEmailAddress: 'Verification email address',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Enter an email address under this domain to receive a code and verify this domain.',
  formFieldLabel__organizationName: 'Name',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Name of passkey',
  formFieldLabel__password: 'Password',
  formFieldLabel__phoneNumber: 'Phone number',
  formFieldLabel__role: 'Role',
  formFieldLabel__signOutOfOtherSessions: 'Sign out of all other devices',
  formFieldLabel__username: 'Username',
  impersonationFab: {
    action__signOut: 'Sign out',
    title: 'Signed in as {{identifier}}',
  },
  lastAuthenticationStrategy: 'Last used',
  maintenanceMode:
    "We are currently undergoing maintenance, but don't worry, it shouldn't take more than a few minutes.",
  membershipRole__admin: 'Admin',
  membershipRole__basicMember: 'Member',
  membershipRole__guestMember: 'Guest',
  organizationList: {
    action__createOrganization: 'Create organisation',
    action__invitationAccept: 'Join',
    action__suggestionsAccept: 'Request to join',
    createOrganization: 'Create Organisation',
    invitationAcceptedLabel: 'Joined',
    subtitle: 'to continue to {{applicationName}}',
    suggestionsAcceptedLabel: 'Pending approval',
    title: 'Choose an account',
    titleWithoutPersonal: 'Choose an organisation',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'Automatic invitations',
    badge__automaticSuggestion: 'Automatic suggestions',
    badge__manualInvitation: 'No automatic enrollment',
    badge__unverified: 'Unverified',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'Add the domain to verify. Users with email addresses at this domain can join the organisation automatically or request to join.',
      title: 'Add domain',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'The invitations could not be sent. There are already pending invitations for the following email addresses: {{email_addresses}}.',
      formButtonPrimary__continue: 'Send invitations',
      selectDropdown__role: 'Select role',
      subtitle: 'Enter or paste one or more email addresses, separated by spaces or commas.',
      successMessage: 'Invitations successfully sent',
      title: 'Invite new members',
    },
    membersPage: {
      action__invite: 'Invite',
      action__search: undefined,
      activeMembersTab: {
        menuAction__remove: 'Remove member',
        tableHeader__actions: 'Actions',
        tableHeader__joined: 'Joined',
        tableHeader__role: 'Role',
        tableHeader__user: 'User',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: "We are updating the available roles. Once that's done, you'll be able to update roles again.",
          title: 'Roles are temporarily locked',
        },
      },
      detailsTitle__emptyRow: 'No members to display',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Invite users by connecting an email domain with your organisation. Anyone who signs up with a matching email domain will be able to join the organisation anytime.',
          headerTitle: 'Automatic invitations',
          primaryButton: 'Manage verified domains',
        },
        table__emptyRow: 'No invitations to display',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Revoke invitation',
        tableHeader__invited: 'Invited',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Users who sign up with a matching email domain, will be able to see a suggestion to request to join your organisation.',
          headerTitle: 'Automatic suggestions',
          primaryButton: 'Manage verified domains',
        },
        menuAction__approve: 'Approve',
        menuAction__reject: 'Reject',
        tableHeader__requested: 'Requested access',
        table__emptyRow: 'No requests to display',
      },
      start: {
        headerTitle__invitations: 'Invitations',
        headerTitle__members: 'Members',
        headerTitle__requests: 'Requests',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'Manage your organisation.',
      general: 'General',
      members: 'Members',
      title: 'Organisation',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Type "{{organizationName}}" below to continue.',
          messageLine1: 'Are you sure you want to delete this organisation?',
          messageLine2: 'This action is permanent and irreversible.',
          successMessage: 'You have deleted the organisation.',
          title: 'Delete organisation',
        },
        leaveOrganization: {
          actionDescription: 'Type "{{organizationName}}" below to continue.',
          messageLine1:
            'Are you sure you want to leave this organisation? You will lose access to this organisation and its applications.',
          messageLine2: 'This action is permanent and irreversible.',
          successMessage: 'You have left the organisation.',
          title: 'Leave organisation',
        },
        title: 'Danger',
      },
      domainSection: {
        menuAction__manage: 'Manage',
        menuAction__remove: 'Delete',
        menuAction__verify: 'Verify',
        primaryButton: 'Add domain',
        subtitle:
          'Allow users to join the organisation automatically or request to join based on a verified email domain.',
        title: 'Verified domains',
      },
      successMessage: 'The organisation has been updated.',
      title: 'Update profile',
    },
    removeDomainPage: {
      messageLine1: 'The email domain {{domain}} will be removed.',
      messageLine2: 'Users won’t be able to join the organisation automatically after this.',
      successMessage: '{{domain}} has been removed.',
      title: 'Remove domain',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: 'Members',
      profileSection: {
        primaryButton: 'Update profile',
        title: 'Organisation Profile',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Removing this domain will affect invited users.',
        removeDomainActionLabel__remove: 'Remove domain',
        removeDomainSubtitle: 'Remove this domain from your verified domains',
        removeDomainTitle: 'Remove domain',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Users are automatically invited to join the organisation when they sign-up and can join anytime.',
        automaticInvitationOption__label: 'Automatic invitations',
        automaticSuggestionOption__description:
          'Users receive a suggestion to request to join, but must be approved by an admin before they are able to join the organisation.',
        automaticSuggestionOption__label: 'Automatic suggestions',
        calloutInfoLabel: 'Changing the enrollment mode will only affect new users.',
        calloutInvitationCountLabel: 'Pending invitations sent to users: {{count}}',
        calloutSuggestionCountLabel: 'Pending suggestions sent to users: {{count}}',
        manualInvitationOption__description: 'Users can only be invited manually to the organisation.',
        manualInvitationOption__label: 'No automatic enrollment',
        subtitle: 'Choose how users from this domain can join the organisation.',
      },
      start: {
        headerTitle__danger: 'Danger',
        headerTitle__enrollment: 'Enrollment options',
      },
      subtitle: 'The domain {{domain}} is now verified. Continue by selecting enrollment mode.',
      title: 'Update {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Enter the verification code sent to your email address',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'The domain {{domainName}} needs to be verified via email.',
      subtitleVerificationCodeScreen: 'A verification code was sent to {{emailAddress}}. Enter the code to continue.',
      title: 'Verify domain',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'Create organisation',
    action__invitationAccept: 'Join',
    action__manageOrganization: 'Manage',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'Request to join',
    notSelected: 'No organisation selected',
    personalWorkspace: 'Personal account',
    suggestionsAcceptedLabel: 'Pending approval',
  },
  paginationButton__next: 'Next',
  paginationButton__previous: 'Previous',
  paginationRowText__displaying: 'Displaying',
  paginationRowText__of: 'of',
  reverification: {
    alternativeMethods: {
      actionLink: 'Get help',
      actionText: 'Don’t have any of these?',
      blockButton__backupCode: 'Use a backup code',
      blockButton__emailCode: 'Email code to {{identifier}}',
      blockButton__passkey: undefined,
      blockButton__password: 'Continue with your password',
      blockButton__phoneCode: 'Send SMS code to {{identifier}}',
      blockButton__totp: 'Use your authenticator app',
      getHelp: {
        blockButton__emailSupport: 'Email support',
        content:
          'If you have trouble verifying your account, email us and we will work with you to restore access as soon as possible.',
        title: 'Get help',
      },
      subtitle: 'Facing issues? You can use any of these methods for verification.',
      title: 'Use another method',
    },
    backupCodeMfa: {
      subtitle: 'Enter the backup code you received when setting up two-step authentication',
      title: 'Enter a backup code',
    },
    emailCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the code sent to your email to continue',
      title: 'Verification required',
    },
    noAvailableMethods: {
      message: 'Cannot proceed with verification. No suitable authentication factor is configured',
      subtitle: 'An error occurred',
      title: 'Cannot verify your account',
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: 'Use another method',
      subtitle: 'Enter your current password to continue',
      title: 'Verification required',
    },
    phoneCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the code sent to your phone to continue',
      title: 'Verification required',
    },
    phoneCodeMfa: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the code sent to your phone to continue',
      title: 'Verification required',
    },
    totpMfa: {
      formTitle: 'Verification code',
      subtitle: 'Enter the code generated by your authenticator app to continue',
      title: 'Verification required',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Add account',
      action__signOutAll: 'Sign out of all accounts',
      subtitle: 'Select the account with which you wish to continue.',
      title: 'Choose an account',
    },
    alternativeMethods: {
      actionLink: 'Get help',
      actionText: 'Don’t have any of these?',
      blockButton__backupCode: 'Use a backup code',
      blockButton__emailCode: 'Email code to {{identifier}}',
      blockButton__emailLink: 'Email link to {{identifier}}',
      blockButton__passkey: 'Sign in with your passkey',
      blockButton__password: 'Sign in with your password',
      blockButton__phoneCode: 'Send SMS code to {{identifier}}',
      blockButton__totp: 'Use your authenticator app',
      getHelp: {
        blockButton__emailSupport: 'Email support',
        content:
          'If you have trouble signing into your account, email us and we will work with you to restore access as soon as possible.',
        title: 'Get help',
      },
      subtitle: 'Facing issues? You can use any of these methods to sign in.',
      title: 'Use another method',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: 'Your backup code is the one you got when setting up two-step authentication.',
      title: 'Enter a backup code',
    },
    emailCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
    },
    emailCodeMfa: {
      formTitle: 'Check your email',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'To continue, open the verification link on the device and browser from which you initiated the sign-in',
        title: 'Verification link is invalid for this device',
      },
      expired: {
        subtitle: 'Return to the original tab to continue.',
        title: 'This verification link has expired',
      },
      failed: {
        subtitle: 'Return to the original tab to continue.',
        title: 'This verification link is invalid',
      },
      formSubtitle: 'Use the verification link sent to your email',
      formTitle: 'Verification link',
      loading: {
        subtitle: 'You will be redirected soon',
        title: 'Signing in...',
      },
      resendButton: "Didn't receive a link? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
      unusedTab: {
        title: 'You may close this tab',
      },
      verified: {
        subtitle: 'You will be redirected soon',
        title: 'Successfully signed in',
      },
      verifiedSwitchTab: {
        subtitle: 'Return to original tab to continue',
        subtitleNewTab: 'Return to the newly opened tab to continue',
        titleNewTab: 'Signed in on other tab',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Use the verification link sent to your email',
      resendButton: "Didn't receive a link? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your email',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'Reset password code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to reset your password',
      subtitle_email: 'First, enter the code sent to your email address',
      subtitle_phone: 'First, enter the code sent to your phone',
      title: 'Reset password',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Reset your password',
      label__alternativeMethods: 'Or, sign in with another method',
      title: 'Forgot Password?',
    },
    newDeviceVerificationNotice:
      "You're signing in from a new device. We're asking for verification to keep your account secure.",
    noAvailableMethods: {
      message: "Cannot proceed with sign in. There's no available authentication factor.",
      subtitle: 'An error occurred',
      title: 'Cannot sign in',
    },
    passkey: {
      subtitle: "Using your passkey confirms it's you. Your device may ask for your fingerprint, face or screen lock.",
      title: 'Use your passkey',
    },
    password: {
      actionLink: 'Use another method',
      subtitle: 'Enter the password associated with your account',
      title: 'Enter your password',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: 'Password compromised',
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Check your phone',
    },
    phoneCodeMfa: {
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'To continue, please enter the verification code sent to your phone',
      title: 'Check your phone',
    },
    resetPassword: {
      formButtonPrimary: 'Reset Password',
      requiredMessage: 'For security reasons, it is required to reset your password.',
      successMessage: 'Your password was successfully changed. Signing you in, please wait a moment.',
      title: 'Set new password',
    },
    resetPasswordMfa: {
      detailsLabel: 'We need to verify your identity before resetting your password.',
    },
    start: {
      actionLink: 'Sign up',
      actionLink__join_waitlist: 'Join waitlist',
      actionLink__use_email: 'Use email',
      actionLink__use_email_username: 'Use email or username',
      actionLink__use_passkey: 'Use passkey instead',
      actionLink__use_phone: 'Use phone',
      actionLink__use_username: 'Use username',
      actionText: 'Don’t have an account?',
      actionText__join_waitlist: 'Want early access?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'Welcome back! Please sign in to continue',
      subtitleCombined: undefined,
      title: 'Sign in to {{applicationName}}',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: 'Verification code',
      subtitle: 'To continue, please enter the verification code generated by your authenticator app',
      title: 'Two-step verification',
    },
    web3Solana: {
      subtitle: 'Select a wallet below to sign in',
      title: 'Sign in with Solana',
    },
  },
  signInEnterPasswordTitle: 'Enter your password',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'Sign in',
      actionText: 'Already have an account?',
      subtitle: 'Please fill in the remaining details to continue.',
      title: 'Fill in missing fields',
    },
    emailCode: {
      formSubtitle: 'Enter the verification code sent to your email address',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the verification code sent to your email',
      title: 'Verify your email',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'To continue, open the verification link on the device and browser from which you initiated the sign-up',
        title: 'Verification link is invalid for this device',
      },
      formSubtitle: 'Use the verification link sent to your email address',
      formTitle: 'Verification link',
      loading: {
        title: 'Signing up...',
      },
      resendButton: "Didn't receive a link? Resend",
      subtitle: 'to continue to {{applicationName}}',
      title: 'Verify your email',
      verified: {
        title: 'Successfully signed up',
      },
      verifiedSwitchTab: {
        subtitle: 'Return to the newly opened tab to continue',
        subtitleNewTab: 'Return to previous tab to continue',
        title: 'Successfully verified email',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'I agree to the {{ privacyPolicyLink || link("Privacy Policy") }}',
        label__onlyTermsOfService: 'I agree to the {{ termsOfServiceLink || link("Terms of Service") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'I agree to the {{ termsOfServiceLink || link("Terms of Service") }} and {{ privacyPolicyLink || link("Privacy Policy") }}',
      },
      continue: {
        subtitle: 'Please read and accept the terms to continue',
        title: 'Legal consent',
      },
    },
    phoneCode: {
      formSubtitle: 'Enter the verification code sent to your phone number',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'Enter the verification code sent to your phone',
      title: 'Verify your phone',
    },
    restrictedAccess: {
      actionLink: 'Sign in',
      actionText: 'Already have an account?',
      blockButton__emailSupport: 'Email support',
      blockButton__joinWaitlist: 'Join waitlist',
      subtitle: 'Sign ups are currently disabled. If you believe you should have access, please contact support.',
      subtitleWaitlist: 'Sign ups are currently disabled. To be the first to know when we launch, join the waitlist.',
      title: 'Access restricted',
    },
    start: {
      actionLink: 'Sign in',
      actionLink__use_email: 'Use email instead',
      actionLink__use_phone: 'Use phone instead',
      actionText: 'Already have an account?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'Welcome! Please fill in the details to get started.',
      subtitleCombined: 'Welcome! Please fill in the details to get started.',
      title: 'Create your account',
      titleCombined: 'Create your account',
    },
    web3Solana: {
      subtitle: 'Select a wallet below to sign up',
      title: 'Sign up with Solana',
    },
  },
  socialButtonsBlockButton: 'Continue with {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'An organisation already exists for the detected company name ({{organizationName}}) and {{organizationDomain}}. Join by invitation.',
    },
    chooseOrganization: {
      action__createOrganization: 'Create new organisation',
      action__invitationAccept: 'Join',
      action__suggestionsAccept: 'Request to join',
      subtitle: 'Join an existing organisation or create a new one',
      subtitle__createOrganizationDisabled: 'Join an existing organisation',
      suggestionsAcceptedLabel: 'Pending approval',
      title: 'Choose an organisation',
    },
    createOrganization: {
      formButtonReset: 'Cancel',
      formButtonSubmit: 'Continue',
      formFieldInputPlaceholder__name: 'My Organisation',
      formFieldInputPlaceholder__slug: 'my-organisation',
      formFieldLabel__name: 'Name',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Enter your organisation details to continue',
      title: 'Setup your organisation',
    },
    organizationCreationDisabled: {
      subtitle: 'Contact your organisation admin for an invitation.',
      title: 'You must belong to an organisation',
    },
    signOut: {
      actionLink: 'Sign out',
      actionText: 'Signed in as {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} is already a member of the organisation.',
    avatar_file_size_exceeded: 'File size exceeds the maximum limit of 10MB. Please choose a smaller file.',
    avatar_file_type_invalid: 'File type not supported. Please upload a JPG, PNG, GIF, or WEBP image.',
    captcha_invalid:
      'Sign up unsuccessful due to failed security validations. Please try using a different browser or disabling browser extensions. If issues persist, contact support for assistance.',
    captcha_unavailable:
      'Sign up unsuccessful due to failed bot validation. Please refresh the page to try again or reach out to support for more assistance.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      'Temporary email services are not supported. Please use your regular email address to create an account.',
    form_identifier_exists__email_address: 'This email address is taken. Please try another.',
    form_identifier_exists__phone_number: 'This phone number is taken. Please try another.',
    form_identifier_exists__username: 'This username is taken. Please try another.',
    form_identifier_not_found: "We couldn't find an account with those details.",
    form_new_password_matches_current: undefined,
    form_param_format_invalid: 'The value entered is in an invalid format. Please check and correct it.',
    form_param_format_invalid__email_address: 'Email address must be a valid email address.',
    form_param_format_invalid__phone_number: 'Phone number must be in a valid international format.',
    form_param_max_length_exceeded__first_name: 'First name should not exceed 256 characters.',
    form_param_max_length_exceeded__last_name: 'Last name should not exceed 256 characters.',
    form_param_max_length_exceeded__name: 'Name should not exceed 256 characters.',
    form_param_nil: 'This field is required and cannot be empty.',
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: 'The value entered is invalid. Please correct it.',
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: 'The password you entered is incorrect. Please try again.',
    form_password_length_too_short: 'Your password is too short. It must be at least 8 characters long.',
    form_password_not_strong_enough: 'Your password is not strong enough.',
    form_password_or_identifier_incorrect: 'Password or email address is incorrect. Try again, or use another method.',
    form_password_pwned:
      'This password has been found as part of a breach and can not be used, please try another password instead.',
    form_password_pwned__sign_in:
      'This password has been found as part of a breach and can not be used, please reset your password.',
    form_password_size_in_bytes_exceeded:
      'Your password has exceeded the maximum number of bytes allowed, please shorten it or remove some special characters.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'Incorrect Password',
    form_username_invalid_character:
      'Your username contains invalid characters. Please use only letters, numbers, and underscores.',
    form_username_invalid_length: 'Your username must be between 3 and 20 characters long.',
    form_username_needs_non_number_char: 'Your username must contain at least one non-numeric character.',
    identification_deletion_failed: 'You cannot delete your last identification.',
    not_allowed_access: undefined,
    organization_domain_blocked: 'This is a blocked email provider domain. Please use a different one.',
    organization_domain_common: 'This is a common email provider domain. Please use a different one.',
    organization_domain_exists_for_enterprise_connection: 'This domain is already used for your organisation’s SSO',
    organization_membership_quota_exceeded:
      'You have reached your limit of organisation memberships, including outstanding invitations.',
    organization_minimum_permissions_needed:
      'There has to be at least one organisation member with the minimum required permissions.',
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'A passkey is already registered with this device.',
    passkey_not_supported: 'Passkeys are not supported on this device.',
    passkey_pa_not_supported: 'Registration requires a platform authenticator but the device does not support it.',
    passkey_registration_cancelled: 'Passkey registration was cancelled or timed out.',
    passkey_retrieval_cancelled: 'Passkey verification was cancelled or timed out.',
    passwordComplexity: {
      maximumLength: 'less than {{length}} characters',
      minimumLength: '{{length}} or more characters',
      requireLowercase: 'a lowercase letter',
      requireNumbers: 'a number',
      requireSpecialCharacter: 'a special character',
      requireUppercase: 'an uppercase letter',
      sentencePrefix: 'Your password must contain',
    },
    phone_number_exists: 'This phone number is taken. Please try another.',
    session_exists: "You're already signed in.",
    web3_missing_identifier: undefined,
    web3_signature_request_rejected: 'You have rejected the signature request. Please try again to continue.',
    web3_solana_signature_generation_failed:
      'An error occurred while generating the signature. Please try again to continue.',
    zxcvbn: {
      couldBeStronger: 'Your password works, but could be stronger. Try adding more characters.',
      goodPassword: 'Your password meets all the necessary requirements.',
      notEnough: 'Your password is not strong enough.',
      suggestions: {
        allUppercase: 'Capitalize some, but not all letters.',
        anotherWord: 'Add more words that are less common.',
        associatedYears: 'Avoid years that are associated with you.',
        capitalization: 'Capitalize more than the first letter.',
        dates: 'Avoid dates and years that are associated with you.',
        l33t: "Avoid predictable letter substitutions like '@' for 'a'.",
        longerKeyboardPattern: 'Use longer keyboard patterns and change typing direction multiple times.',
        noNeed: 'You can create strong passwords without using symbols, numbers, or uppercase letters.',
        pwned: 'If you use this password elsewhere, you should change it.',
        recentYears: 'Avoid recent years.',
        repeated: 'Avoid repeated words and characters.',
        reverseWords: 'Avoid reversed spellings of common words.',
        sequences: 'Avoid common character sequences.',
        useWords: 'Use multiple words, but avoid common phrases.',
      },
      warnings: {
        common: 'This is a commonly used password.',
        commonNames: 'Common names and surnames are easy to guess.',
        dates: 'Dates are easy to guess.',
        extendedRepeat: 'Repeated character patterns like "abcabcabc" are easy to guess.',
        keyPattern: 'Short keyboard patterns are easy to guess.',
        namesByThemselves: 'Single names or surnames are easy to guess.',
        pwned: 'Your password was exposed by a data breach on the Internet.',
        recentYears: 'Recent years are easy to guess.',
        sequences: 'Common character sequences like "abc" are easy to guess.',
        similarToCommon: 'This is similar to a commonly used password.',
        simpleRepeat: 'Repeated characters like "aaa" are easy to guess.',
        straightRow: 'Straight rows of keys on your keyboard are easy to guess.',
        topHundred: 'This is a frequently used password.',
        topTen: 'This is a heavily used password.',
        userInputs: 'There should not be any personal or page related data.',
        wordByItself: 'Single words are easy to guess.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Add account',
    action__closeUserMenu: undefined,
    action__manageAccount: 'Manage account',
    action__openUserMenu: undefined,
    action__signOut: 'Sign out',
    action__signOutAll: 'Sign out of all accounts',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: 'Copied!',
      actionLabel__copy: 'Copy all',
      actionLabel__download: 'Download .txt',
      actionLabel__print: 'Print',
      infoText1: 'Backup codes will be enabled for this account.',
      infoText2:
        'Keep the backup codes secret and store them securely. You may regenerate backup codes if you suspect they have been compromised.',
      subtitle__codelist: 'Store them securely and keep them secret.',
      successMessage:
        'Backup codes are now enabled. You can use one of these to sign in to your account, if you lose access to your authentication device. Each code can only be used once.',
      successSubtitle:
        'You can use one of these to sign in to your account, if you lose access to your authentication device.',
      title: 'Add backup code verification',
      title__codelist: 'Backup codes',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: 'Select a provider to connect your account.',
      formHint__noAccounts: 'There are no available external account providers.',
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2:
          'You will no longer be able to use this connected account and any dependent features will no longer work.',
        successMessage: '{{connectedAccount}} has been removed from your account.',
        title: 'Remove connected account',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'The provider has been added to your account',
      title: 'Add connected account',
    },
    deletePage: {
      actionDescription: 'Type "Delete account" below to continue.',
      confirm: 'Delete account',
      messageLine1:
        'Are you sure you want to delete your account? Some associated data may be retained. To request full data deletion, please contact support.',
      messageLine2: 'This action is permanent and irreversible.',
      title: 'Delete account',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'An email containing a verification code will be sent to this email address.',
        formSubtitle: 'Enter the verification code sent to {{identifier}}',
        formTitle: 'Verification code',
        resendButton: "Didn't receive a code? Resend",
        successMessage: 'The email {{identifier}} has been added to your account.',
      },
      emailLink: {
        formHint: 'An email containing a verification link will be sent to this email address.',
        formSubtitle: 'Click on the verification link in the email sent to {{identifier}}',
        formTitle: 'Verification link',
        resendButton: "Didn't receive a link? Resend",
        successMessage: 'The email {{identifier}} has been added to your account.',
      },
      enterpriseSSOLink: {
        formButton: undefined,
        formSubtitle: undefined,
      },
      formHint: undefined,
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2: 'You will no longer be able to sign in using this email address.',
        successMessage: '{{emailAddress}} has been removed from your account.',
        title: 'Remove email address',
      },
      title: 'Add email address',
      verifyTitle: 'Verify email address',
    },
    formButtonPrimary__add: 'Add',
    formButtonPrimary__continue: 'Continue',
    formButtonPrimary__finish: 'Finish',
    formButtonPrimary__remove: 'Remove',
    formButtonPrimary__save: 'Save',
    formButtonReset: 'Cancel',
    mfaPage: {
      formHint: 'Select a method to add.',
      title: 'Add two-step verification',
    },
    mfaPhoneCodePage: {
      backButton: 'Use existing number',
      primaryButton__addPhoneNumber: 'Add phone number',
      removeResource: {
        messageLine1: '{{identifier}} will be no longer receiving verification codes when signing in.',
        messageLine2: 'Your account may not be as secure. Are you sure you want to continue?',
        successMessage: 'SMS code two-step verification has been removed for {{mfaPhoneCode}}',
        title: 'Remove two-step verification',
      },
      subtitle__availablePhoneNumbers:
        'Select an existing phone number to register for SMS code two-step verification or add a new one.',
      subtitle__unavailablePhoneNumbers:
        'There are no available phone numbers to register for SMS code two-step verification, please add a new one.',
      successMessage1:
        'When signing in, you will need to enter a verification code sent to this phone number as an additional step.',
      successMessage2:
        'Save these backup codes and store them somewhere safe. If you lose access to your authentication device, you can use backup codes to sign in.',
      successTitle: 'SMS code verification enabled',
      title: 'Add SMS code verification',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Scan QR code instead',
        buttonUnableToScan__nonPrimary: 'Can’t scan QR code?',
        infoText__ableToScan:
          'Set up a new sign-in method in your authenticator app and scan the following QR code to link it to your account.',
        infoText__unableToScan: 'Set up a new sign-in method in your authenticator and enter the Key provided below.',
        inputLabel__unableToScan1:
          'Make sure Time-based or One-time passwords is enabled, then finish linking your account.',
        inputLabel__unableToScan2:
          'Alternatively, if your authenticator supports TOTP URIs, you can also copy the full URI.',
      },
      removeResource: {
        messageLine1: 'Verification codes from this authenticator will no longer be required when signing in.',
        messageLine2: 'Your account may not be as secure. Are you sure you want to continue?',
        successMessage: 'Two-step verification via authenticator application has been removed.',
        title: 'Remove two-step verification',
      },
      successMessage:
        'Two-step verification is now enabled. When signing in, you will need to enter a verification code from this authenticator as an additional step.',
      title: 'Add authenticator application',
      verifySubtitle: 'Enter verification code generated by your authenticator',
      verifyTitle: 'Verification code',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Profile',
      apiKeys: undefined,
      billing: undefined,
      description: 'Manage your account info.',
      security: 'Security',
      title: 'Account',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} will be removed from this account.',
        title: 'Remove passkey',
      },
      subtitle__rename: 'You can change the passkey name to make it easier to find.',
      title__rename: 'Rename Passkey',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'It is recommended to sign out of all other devices which may have used your old password.',
      readonly: 'Your password can currently not be edited because you can sign in only via the enterprise connection.',
      successMessage__set: 'Your password has been set.',
      successMessage__signOutOfOtherSessions: 'All other devices have been signed out.',
      successMessage__update: 'Your password has been updated.',
      title__set: 'Set password',
      title__update: 'Update password',
    },
    phoneNumberPage: {
      infoText:
        'A text message containing a verification code will be sent to this phone number. Message and data rates may apply.',
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2: 'You will no longer be able to sign in using this phone number.',
        successMessage: '{{phoneNumber}} has been removed from your account.',
        title: 'Remove phone number',
      },
      successMessage: '{{identifier}} has been added to your account.',
      title: 'Add phone number',
      verifySubtitle: 'Enter the verification code sent to {{identifier}}',
      verifyTitle: 'Verify phone number',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'Recommended size 1:1, up to 10MB.',
      imageFormDestructiveActionSubtitle: 'Remove',
      imageFormSubtitle: 'Upload',
      imageFormTitle: 'Profile image',
      readonly: 'Your profile information has been provided by the enterprise connection and cannot be edited.',
      successMessage: 'Your profile has been updated.',
      title: 'Update profile',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Sign out of device',
        title: 'Active devices',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Reconnect',
        actionLabel__reauthorize: 'Authorise now',
        destructiveActionTitle: 'Remove',
        primaryButton: 'Connect account',
        subtitle__disconnected: 'This account has been disconnected.',
        subtitle__reauthorize:
          'The required scopes have been updated, and you may be experiencing limited functionality. Please re-authorise this application to avoid any issues',
        title: 'Connected accounts',
      },
      dangerSection: {
        deleteAccountButton: 'Delete account',
        title: 'Delete account',
      },
      emailAddressesSection: {
        destructiveAction: 'Remove email',
        detailsAction__nonPrimary: 'Set as primary',
        detailsAction__primary: 'Complete verification',
        detailsAction__unverified: 'Verify',
        primaryButton: 'Add email address',
        title: 'Email addresses',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Connect account',
        title: 'Enterprise accounts',
      },
      headerTitle__account: 'Profile details',
      headerTitle__security: 'Security',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Regenerate',
          headerTitle: 'Backup codes',
          subtitle__regenerate:
            'Get a fresh set of secure backup codes. Prior backup codes will be deleted and cannot be used.',
          title__regenerate: 'Regenerate backup codes',
        },
        phoneCode: {
          actionLabel__setDefault: 'Set as default',
          destructiveActionLabel: 'Remove',
        },
        primaryButton: 'Add two-step verification',
        title: 'Two-step verification',
        totp: {
          destructiveActionTitle: 'Remove',
          headerTitle: 'Authenticator application',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Remove',
        menuAction__rename: 'Rename',
        primaryButton: undefined,
        title: 'Passkeys',
      },
      passwordSection: {
        primaryButton__setPassword: 'Set password',
        primaryButton__updatePassword: 'Update password',
        title: 'Password',
      },
      phoneNumbersSection: {
        destructiveAction: 'Remove phone number',
        detailsAction__nonPrimary: 'Set as primary',
        detailsAction__primary: 'Complete verification',
        detailsAction__unverified: 'Verify phone number',
        primaryButton: 'Add phone number',
        title: 'Phone numbers',
      },
      profileSection: {
        primaryButton: 'Update profile',
        title: 'Profile',
      },
      usernameSection: {
        primaryButton__setUsername: 'Set username',
        primaryButton__updateUsername: 'Update username',
        title: 'Username',
      },
      web3WalletsSection: {
        destructiveAction: 'Remove wallet',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'Connect wallet',
        title: 'Web3 wallets',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Select a Solana wallet to connect to your account.',
          title: 'Add a Solana wallet',
        },
      },
    },
    usernamePage: {
      successMessage: 'Your username has been updated.',
      title__set: 'Set username',
      title__update: 'Update username',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} will be removed from this account.',
        messageLine2: 'You will no longer be able to sign in using this web3 wallet.',
        successMessage: '{{web3Wallet}} has been removed from your account.',
        title: 'Remove web3 wallet',
      },
      subtitle__availableWallets: 'Select a web3 wallet to connect to your account.',
      subtitle__unavailableWallets: 'There are no available web3 wallets.',
      successMessage: 'The wallet has been added to your account.',
      title: 'Add web3 wallet',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Sign in',
      actionText: 'Already have access?',
      formButton: 'Join the waitlist',
      subtitle: 'Enter your email address and we’ll let you know when your spot is ready',
      title: 'Join the waitlist',
    },
    success: {
      message: 'You will be redirected soon...',
      subtitle: 'We’ll be in touch when your spot is ready',
      title: 'Thanks for joining the waitlist!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Connect with {{walletName}}',
    continue: 'Continue with {{walletName}}',
    noneAvailable:
      'No Solana Web3 wallets detected. Please install a Web3 supported {{ solanaWalletsLink || link("wallet extension") }}.',
  },
} as const;
